/**
 * constants.ts — single source of truth for values repeated across the
 * codebase as string literals or magic numbers.
 *
 * Audit reference: code-health audit 2026-05-14, items #14/#15/#9.
 *
 * Guidelines:
 *  - Add here only when the value is referenced in 2+ files.
 *  - URLs and domain names live here even when used once, because they're
 *    deploy-environment-sensitive (preview deploys leak prod URLs otherwise).
 *  - Lead-value tiers live here because the same tier is used across
 *    captureEmail() callers + ad-platform reporting code.
 */

// ── Canonical site URLs ───────────────────────────────────────
// Used for share URLs, canonical tags, OG metadata, schema.org JSON-LD.
export const SITE_URL = "https://www.bodyhackguide.co";
export const SITE_NAME = "BodyHackGuide";

// ── Exact content counts ─────────────────────────────────────
// The number of compound profiles in the database when it was last counted by
// hand (273 rows, 2026-10-20; the table held 286 on 2026-10-21). Pages must
// show an exact number, never a rounded "250+".
//
// Visible copy no longer prints this constant: it reads the live count
// (useCompoundCount / LiveCompoundCount, or the length of the list a page has
// already loaded). The homepage hero and its static shell in index.html say
// "every compound we track" with no number, because the shell cannot read the
// database and a typed number goes stale.
//
// What still uses this value: the Organization JSON-LD (lib/authorSchema.ts),
// the /wiki title and meta description while the list loads, and the fallback
// when the live count request fails. Static copies that cannot import it and
// still say 273: index.html (noscript fallback), scripts/generate-prerender-
// stubs.mjs, public/llms.txt and public/llms-full.txt.
export const COMPOUND_COUNT = 273;

// ── Price alerts ──────────────────────────────────────────────
// Off. The checker (supabase/functions/check-price-drops) exists, but no scheduled job calls it, so "Set Alert"
// promised an email that never came. Turn this on only after a job runs the checker and a test alert has arrived
// in an inbox. While it is off the alert buttons on /compare and the compound page are not drawn, and Account
// Settings lists existing alerts only so they can be removed.
export const PRICE_ALERTS_ENABLED = false;

// ── Default vendor coupons ────────────────────────────────────
// These are the universal coupon codes baked into the affiliate links.
// Rotating them here updates every UI surface (AderaLaunchPopup, footer
// callouts, OnboardWizard placeholder).
export const ADERA_DEFAULT_COUPON = "BODYHACKGUIDE";

// ── Lead value tiers (Meta / GA4 / Google Ads VALUE-based bidding) ────
// captureEmail() reads these via trackLead. Higher value = higher intent,
// gives ad platforms a richer optimization signal so they bid more for
// users likely to convert into lead-magnet downloads or coaching waitlists.
export const LEAD_VALUE_TIERS = {
  /** Newsletter signup with no specific deliverable (footer, hero, generic) */
  newsletter: 20,
  /** Cheatsheet / PDF / data-download lead magnets */
  leadMagnet: 35,
  /** Quiz completion — high intent (multi-step questionnaire) */
  quiz: 35,
  /** Tool-deliverable (cost-calc PDF, intranasal calc breakdown) */
  toolDeliverable: 25,
  /** Coaching waitlist — highest-value lead (1:1 paid product) */
  coaching: 50,
} as const;

export type LeadValueTier = keyof typeof LEAD_VALUE_TIERS;
